import React from "react";
import { createRoot } from "react-dom/client";
import { initObservability } from "./lib/observability";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthGate, AuthProvider } from "./lib/auth";
import ClientDetailPage from "./pages/ClientDetail";
import ClientsPage from "./pages/Clients";
import CompliancePage from "./pages/Compliance";
import DashboardPage from "./pages/Dashboard";
import DisputesPage from "./pages/Disputes";
import FilingReviewPage from "./pages/FilingReview";
import StartIntakePage from "./pages/StartIntake";
import IntakePage from "./pages/Intake";
import LettersPage from "./pages/Letters";
import LoginPage from "./pages/Login";
import PasswordUpdateRequiredPage from "./pages/PasswordUpdateRequired";
import MailPage from "./pages/Mail";
import NewClientPage from "./pages/NewClient";
import PortalAgreementSignPage from "./pages/PortalAgreementSign";
import PortalInvoicePayPage from "./pages/PortalInvoicePay";
import PortalDashboardPage from "./pages/PortalDashboard";
import PortalHistoryPage from "./pages/PortalHistory";
import PortalLoginPage from "./pages/PortalLogin";
import PortalSetPasswordPage from "./pages/PortalSetPassword";
import PortalSentPage from "./pages/PortalSent";
import PortalUploadPage from "./pages/PortalUpload";
import PortalVerifyPage from "./pages/PortalVerify";
import BillingPage from "./pages/Billing";
import ReportsPage from "./pages/Reports";
import { StubPage } from "./pages/Stub";
import AffiliatesPage from "./pages/Affiliates";
import { AffiliateLoginPage, AffiliateDashboardPage, AffiliateResetPage } from "./pages/AffiliatePortal";
import JoinFunnelPage from "./pages/JoinFunnel";
import PortalMessagesPage from "./pages/PortalMessages";
import IntegrationsPage from "./pages/Integrations";
import SettingsPage from "./pages/Settings";
import TrainingPage from "./pages/Training";
import AdminPage from "./pages/Admin";
import JoinPage from "./pages/Join";
import SuspendedPage from "./pages/Suspended";
import StaffInvitePage from "./pages/StaffInvite";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPassword";
import "./styles.css";

/**
 * Subdomain isolation:
 *
 * The same Vite bundle is served on app.aliefdisputehub.com and
 * portal.aliefdisputehub.com (Cloudflare Worker custom_domain routes both
 * hostnames at the same Worker, which falls back to index.html for any path
 * via not_found_handling = single-page-application).
 *
 * Without runtime host detection, a visitor to portal.* would see the staff
 * route tree (including AuthGate-protected /clients, /letters, /dashboard,
 * etc.). Even though those routes redirect anonymous users to /login, the
 * staff sign-in form itself leaks the existence of the staff app and — much
 * worse — anyone who has a stale `nedm_session` cookie on portal.* (e.g. a
 * shared-device scenario, or a future cookie-handling bug) would see staff
 * data on a URL the client is expected to use.
 *
 * Fix: detect hostname at startup, render a portal-only <Routes> tree on
 * portal.*, and the existing staff tree everywhere else (app.*, apex,
 * workers.dev fallback, localhost for dev).
 *
 * Defensive: portal.* MUST NOT mount /dashboard, /clients, /letters, etc.
 * even if a staff cookie is somehow present. The portal tree below only
 * knows about /portal/*.
 */
const isPortalHost =
  typeof window !== "undefined" &&
  /^portal\./i.test(window.location.hostname);

function PortalApp() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Client portal routes only. No AuthGate (staff), no AuthProvider:
            the portal uses its own cookie (`nedm_portal`) and the
            PortalDashboard / PortalUpload / PortalHistory pages each call
            the portal API directly and redirect to /portal/login on 401. */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />
        <Route path="/portal/sent" element={<PortalSentPage />} />
        <Route path="/portal/verify" element={<PortalVerifyPage />} />
        <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
        <Route path="/portal/upload" element={<PortalUploadPage />} />
        <Route path="/portal/history" element={<PortalHistoryPage />} />
        <Route path="/portal/messages" element={<PortalMessagesPage />} />
        <Route path="/portal/agreements/sign" element={<PortalAgreementSignPage />} />
        <Route path="/portal/invoice" element={<PortalInvoicePayPage />} />
        {/* Client filing review + attestation — token-keyed public page. */}
        <Route path="/filing/:token" element={<FilingReviewPage />} />
        <Route path="/start/:slug" element={<StartIntakePage />} />
        {/* Affiliate partner pages are public/own-auth, so they exist on both hosts. */}
        <Route path="/with/:slug" element={<JoinFunnelPage />} />
        <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
        <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
        <Route path="/affiliate/reset" element={<AffiliateResetPage />} />

        {/* Root and everything else on portal.* → portal login.
            We deliberately do NOT mount /login, /dashboard, /clients,
            /letters, /reports, /disputes, /mail, /compliance, /billing,
            /referrals, or /integrations here. A visitor who lands on any
            of those paths gets bounced to the client sign-in. */}
        <Route path="/" element={<Navigate to="/portal/login" replace />} />
        <Route path="*" element={<Navigate to="/portal/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function StaffApp() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* PUBLIC */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/staff-invite/:token" element={<StaffInvitePage />} />
          <Route path="/intake/:token" element={<IntakePage />} />
          <Route path="/filing/:token" element={<FilingReviewPage />} />
          <Route path="/start/:slug" element={<StartIntakePage />} />

          {/* CLIENT PORTAL — cookie-authed, no staff AuthGate.
              Kept on the staff hostname as a defensive fallback (e.g. an old
              magic-link email pointing at app.* still resolves), though the
              canonical portal URL is portal.*. */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />
          <Route path="/portal/sent" element={<PortalSentPage />} />
          <Route path="/portal/verify" element={<PortalVerifyPage />} />
          <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
          <Route path="/portal/upload" element={<PortalUploadPage />} />
          <Route path="/portal/history" element={<PortalHistoryPage />} />
        <Route path="/portal/messages" element={<PortalMessagesPage />} />
          <Route path="/portal/agreements/sign" element={<PortalAgreementSignPage />} />
        <Route path="/portal/invoice" element={<PortalInvoicePayPage />} />

          {/* AUTHED */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<AuthGate><DashboardPage /></AuthGate>} />
          <Route path="/clients" element={<AuthGate><ClientsPage /></AuthGate>} />
          <Route path="/clients/new" element={<AuthGate><NewClientPage /></AuthGate>} />
          <Route path="/clients/:id" element={<AuthGate><ClientDetailPage /></AuthGate>} />
          <Route path="/reports" element={<AuthGate><ReportsPage /></AuthGate>} />
          <Route path="/disputes" element={<AuthGate><DisputesPage /></AuthGate>} />
          <Route path="/letters" element={<AuthGate><LettersPage /></AuthGate>} />
          <Route path="/mail" element={<AuthGate><MailPage /></AuthGate>} />
          <Route path="/billing" element={<AuthGate><BillingPage /></AuthGate>} />
          <Route path="/affiliates" element={<AuthGate><AffiliatesPage /></AuthGate>} />
          <Route path="/referrals" element={<Navigate to="/affiliates" replace />} />
          <Route
            path="/integrations"
            element={<AuthGate><IntegrationsPage /></AuthGate>}
          />
          <Route path="/compliance" element={<AuthGate><CompliancePage /></AuthGate>} />
          <Route path="/password-update" element={<AuthGate><PasswordUpdateRequiredPage /></AuthGate>} />
          <Route path="/training" element={<AuthGate><TrainingPage /></AuthGate>} />
          <Route path="/settings" element={<AuthGate><SettingsPage /></AuthGate>} />
          <Route path="/admin" element={<AuthGate><AdminPage /></AuthGate>} />
          {/* Public — a new company accepts an invite (no staff auth). */}
          <Route path="/join/:token" element={<JoinPage />} />
          {/* Affiliate program — partner portal + branded funnel (public, own auth). */}
          <Route path="/with/:slug" element={<JoinFunnelPage />} />
          <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
          <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
          <Route path="/affiliate/reset" element={<AffiliateResetPage />} />
          {/* Shown when a company's access is paused (402 company_suspended). */}
          <Route path="/suspended" element={<SuspendedPage />} />

          {/* fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function App() {
  return isPortalHost ? <PortalApp /> : <StaffApp />;
}

/**
 * PWA install support. The same bundle serves both hostnames, so we inject the
 * correct manifest + iOS home-screen icon/title at runtime by host: the client
 * portal gets a neutral, white-label-safe "Client Portal" identity; the staff
 * app gets the Alief Dispute Hub branding. Then register the offline-shell worker.
 */
(function setupPwa() {
  if (typeof document === "undefined") return;
  const portal = isPortalHost;
  const add = (tag: string, attrs: Record<string, string>) => {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  };
  add("link", { rel: "manifest", href: portal ? "/portal.webmanifest" : "/company.webmanifest" });
  add("link", { rel: "apple-touch-icon", href: portal ? "/icons/portal-apple-180.png" : "/icons/app-apple-180.png" });
  add("meta", { name: "apple-mobile-web-app-title", content: portal ? "Client Portal" : "Alief Dispute Hub" });
  add("meta", { name: "application-name", content: portal ? "Client Portal" : "Alief Dispute Hub" });
})();

if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

initObservability();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
